import { titleSchema, descriptionSchema, categorySchema, locationSchema } from "./ad-rules";
import { assertAdmin } from "@/lib/admin-guard.server";
import { type PlanId } from "./plans";
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";


/** Returnerar den inloggade användarens roll (null om ingen). */
export const getMyRole = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data } = await context.supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", context.userId);
    const roles = (data ?? []).map((r: { role: string }) => r.role);
    const role = roles.includes("superadmin")
      ? "superadmin"
      : roles.includes("admin")
        ? "admin"
        : null;
    return { role, isAdmin: role !== null };
  });

export const getAdminStats = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context as any);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const count = async (table: "ads" | "messages" | "saved_places") => {
      const q: any = supabaseAdmin.from(table).select("id", { count: "exact", head: true });
      const { count: c } = await q;
      return (c as number | null) ?? 0;
    };

    const since = new Date(Date.now() - 7 * 24 * 3600 * 1000).toISOString();
    const { count: adsWeek } = await supabaseAdmin
      .from("ads")
      .select("id", { count: "exact", head: true })
      .gte("created_at", since);

    const { data: users } = await supabaseAdmin.auth.admin.listUsers({ page: 1, perPage: 1 });

    return {
      ads: await count("ads"),
      messages: await count("messages"),
      savedPlaces: await count("saved_places"),
      adsLastWeek: adsWeek ?? 0,
      users: (users as any)?.total ?? undefined,
    };
  });

const AD_SORTS = [
  "created_at",
  "updated_at",
  "expires_at",
  "title",
  "price",
  "category",
  "location",
] as const;

export const listAdminAds = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z
      .object({
        search: z.string().max(80).optional(),
        filter: z
          .enum(["all", "paused", "sold", "active", "expired", "promoted", "hidden"])
          .optional(),
        category: z.string().max(40).optional(),
        userId: z.string().uuid().optional(),
        seller: z.enum(["private", "business"]).optional(),
        sort: z.enum(AD_SORTS).optional(),
        dir: z.enum(["asc", "desc"]).optional(),
        page: z.number().int().min(0).optional(),
        limit: z.number().int().min(1).max(200).optional(),
      })
      .parse(input ?? {}),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context as any);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { resolveDisplayNames } = await import("@/lib/user-names.server");

    const limit = data.limit ?? 50;
    const page = data.page ?? 0;
    const from = page * limit;
    const nowIso = new Date().toISOString();

    let q = supabaseAdmin
      .from("ads")
      .select(
        "slug, title, price, category, location, created_at, updated_at, expires_at, is_paused, hidden, sold_at, archived_at, promoted_until, image_url, image_urls, contact_name, contact_email, user_id",
        { count: "exact" },
      )
      .order(data.sort ?? "created_at", { ascending: data.dir === "asc" })
      .range(from, from + limit - 1);

    if (data.search) {
      const s = data.search.replace(/[%,]/g, " ").trim();
      q = q.or(
        `title.ilike.%${s}%,location.ilike.%${s}%,contact_name.ilike.%${s}%,contact_email.ilike.%${s}%,slug.ilike.%${s}%`,
      );
    }
    if (data.category) q = q.eq("category", data.category);
    if (data.userId) q = q.eq("user_id", data.userId);
    const { data: bizRows } = await supabaseAdmin.from("business_profiles").select("user_id, company_name");
    const bizMap = new Map((bizRows ?? []).map((b: any) => [b.user_id as string, b.company_name as string]));
    if (data.seller === "business") {
      if (!bizMap.size) return { rows: [], total: 0 } as any;
      q = q.in("user_id", [...bizMap.keys()]);
    } else if (data.seller === "private" && bizMap.size) {
      q = q.not("user_id", "in", `(${[...bizMap.keys()].join(",")})`);
    }

    const filter = data.filter ?? "all";
    if (filter === "paused") q = q.eq("is_paused", true);
    if (filter === "hidden") q = q.eq("hidden", true);
    if (filter === "sold") q = q.not("sold_at", "is", null);
    if (filter === "expired") q = q.lt("expires_at", nowIso);
    if (filter === "promoted") q = q.gt("promoted_until", nowIso);
    if (filter === "active") {
      q = q
        .eq("is_paused", false)
        .is("sold_at", null)
        .is("archived_at", null)
        .gte("expires_at", nowIso);
    }

    const { data: ads, error, count } = await q;
    if (error) throw new Error(error.message);

    const rows = ads ?? [];
    const names = await resolveDisplayNames(
      rows.map((a: any) => a.user_id).filter(Boolean) as string[],
    );
    return {
      rows: rows.map((a: any) => ({
        ...a,
        owner_name: a.user_id ? (names.get(a.user_id)?.name ?? null) : null,
        company_name: a.user_id ? (bizMap.get(a.user_id) ?? null) : null,
      })),
      total: count ?? rows.length,
      page,
      limit,
    };
  });


/** Uppdaterar en annons från administrationen. */
export const adminUpdateAd = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z
      .object({
        slug: z.string().min(1),
        title: titleSchema,
        description: descriptionSchema,
        price: z.number().int().min(0).max(100_000_000),
        category: categorySchema,
        location: locationSchema,
        is_paused: z.boolean(),
      })
      .parse(input),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context as never);
    // Samma fält- och textregler som säljarens redigering. Skillnaden mot
    // säljarflödet är bara att admin slipper missbruksspärr och plankvot.
    const { guardText } = await import("./ad-guard.server");
    await guardText(String(data.title), String(data.description), context.userId);

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { slug, ...patch } = data;
    const since = new Date(Date.now() - 5000).toISOString();
    const { error } = await supabaseAdmin
      .from("ads")
      .update(patch as never)
      .eq("slug", slug);
    if (error) throw new Error(error.message);
    const { stampAdminHistory } = await import("./ad-history.server");
    await stampAdminHistory(slug, context.userId, since);
    return { ok: true };
  });



export const adminDeleteAd = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => z.object({ slug: z.string().min(1) }).parse(input))
  .handler(async ({ data, context }) => {
    await assertAdmin(context as any);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { error } = await supabaseAdmin.from("ads").delete().eq("slug", data.slug);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const listAdminUsers = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context as any);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data, error } = await supabaseAdmin.auth.admin.listUsers({ page: 1, perPage: 100 });
    if (error) throw new Error(error.message);
    const { data: roles } = await supabaseAdmin.from("user_roles").select("user_id, role");
    const roleMap = new Map<string, string[]>();
    for (const r of roles ?? []) {
      roleMap.set(r.user_id, [...(roleMap.get(r.user_id) ?? []), r.role]);
    }
    return (data.users ?? []).map((u) => ({
      id: u.id,
      email: u.email ?? "",
      created_at: u.created_at,
      last_sign_in_at: u.last_sign_in_at ?? null,
      confirmed: !!u.email_confirmed_at,
      roles: roleMap.get(u.id) ?? [],
    }));
  });

/** Sidindelad lista över alla användare med roll, kontotyp och antal annonser. */
export const adminListUsersPaged = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z
      .object({
        search: z.string().max(120).optional(),
        page: z.number().int().min(1).max(500).default(1),
        pageSize: z.number().int().min(10).max(100).default(25),
        sort: z
          .enum([
            "display_name",
            "email",
            "plan",
            "provider",
            "ads_count",
            "created_at",
            "last_sign_in_at",
            "consent_accepted_at",
            "consent",
          ])
          .default("created_at"),
        dir: z.enum(["asc", "desc"]).default("desc"),
      })
      .parse(input ?? {}),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context as any);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: rows, error } = await supabaseAdmin.rpc("admin_list_users", {
      _search: data.search ?? undefined,
      _limit: data.pageSize,
      _offset: (data.page - 1) * data.pageSize,
      _actor: context.userId,
      _sort: data.sort,
      _dir: data.dir,
    });

    if (error) throw new Error(error.message);
    const list = (rows ?? []) as any[];

    // Senaste kontotypsändringen per användare avgör om nivån är betald
    // eller något vi själva har lagt till – priset får aldrig gissas.
    const ids = list.map((u) => u.user_id as string);
    const { data: history } = ids.length
      ? await supabaseAdmin
          .from("plan_history")
          .select("user_id, paid, source, billing, created_at")
          .in("user_id", ids)
          .order("created_at", { ascending: false })
      : { data: [] as any[] };
    const latest = new Map<string, any>();
    for (const h of (history ?? []) as any[]) {
      if (!latest.has(h.user_id)) latest.set(h.user_id, h);
    }

    // Enhetsinformation per användare (iOS/Android/web).
    const { data: devices } = ids.length
      ? await supabaseAdmin.from("user_devices").select("user_id, platform, last_seen_at").in("user_id", ids)
      : { data: [] as any[] };
    const deviceMap = new Map<string, { platforms: string[]; last_seen_at: string | null; has_app: boolean }>();
    for (const d of (devices ?? []) as any[]) {
      const existing = deviceMap.get(d.user_id);
      const platforms = new Set(existing ? existing.platforms : []);
      platforms.add(d.platform);
      const last = existing?.last_seen_at && new Date(existing.last_seen_at) > new Date(d.last_seen_at)
        ? existing.last_seen_at
        : d.last_seen_at;
      deviceMap.set(d.user_id, {
        platforms: Array.from(platforms),
        last_seen_at: last,
        has_app: Array.from(platforms).some((p) => p === "ios" || p === "android"),
      });
    }

    return {
      users: list.map((u) => ({
        paid: !!latest.get(u.user_id as string)?.paid,
        plan_source: (latest.get(u.user_id as string)?.source as string) ?? null,
        billing: (latest.get(u.user_id as string)?.billing as string) ?? null,
        id: u.user_id as string,
        email: u.email as string,
        display_name: (u.display_name as string) ?? null,
        created_at: u.created_at as string,
        last_sign_in_at: (u.last_sign_in_at as string) ?? null,
        confirmed: !!u.confirmed,
        roles: (u.roles ?? []) as string[],
        plan: (u.plan ?? "free") as string,
        current_period_end: (u.current_period_end as string) ?? null,
        ads_count: Number(u.ads_count ?? 0),
        consent_terms_version: (u.consent_terms_version as string) ?? null,
        consent_accepted_at: (u.consent_accepted_at as string) ?? null,
        providers: ((u.providers as string[] | null) ?? ["email"]) as string[],
        devices: deviceMap.get(u.user_id as string) ?? { platforms: [], last_seen_at: null, has_app: false },
      })),

      total: Number(list[0]?.total_count ?? 0),
      page: data.page,
      pageSize: data.pageSize,
    };
  });

/** Summering av hur många användare som godkänt aktuella villkor. */
export const adminConsentSummary = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context as any);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { TERMS_VERSION, PRIVACY_VERSION } = await import("./legal-versions");

    const { data: consents } = await supabaseAdmin
      .from("user_consents")
      .select("user_id")
      .eq("terms_version", TERMS_VERSION)
      .eq("privacy_version", PRIVACY_VERSION);

    const accepted = new Set((consents ?? []).map((c: any) => c.user_id as string)).size;

    const { data: usersPage } = await supabaseAdmin.auth.admin.listUsers({ page: 1, perPage: 1 });
    const total = (usersPage as any)?.total ?? accepted;

    return { accepted, total: Number(total) || accepted, version: TERMS_VERSION };
  });

/** Sätter kontotyp för en användare, valfritt under ett visst antal månader. */
export const adminSetUserPlan = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z
      .object({
        userId: z.string().uuid(),
        plan: z.enum(["free", "plus", "pro", "expert"]),
        months: z.number().int().min(0).max(36).default(0),
        // Exakt slutdatum (ISO). Har företräde framför months när det anges.
        periodEnd: z.string().datetime().nullable().optional(),
        paid: z.boolean().optional(),
        billing: z.enum(["monthly", "yearly"]).default("monthly"),
        notify: z.boolean().optional(),
        noticeSubject: z.string().trim().min(3).max(160).optional(),
        noticeBody: z.string().trim().min(10).max(4000).optional(),
      })
      .parse(input),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context as any);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    let periodEnd: string | null = null;
    if (data.plan !== "free") {
      if (data.periodEnd) {
        periodEnd = new Date(data.periodEnd).toISOString();
      } else if (data.months > 0) {
        const end = new Date();
        end.setMonth(end.getMonth() + data.months);
        periodEnd = end.toISOString();
      }
    }

    const { data: before } = await supabaseAdmin
      .from("user_plans")
      .select("plan")
      .eq("user_id", data.userId)
      .maybeSingle();

    const { error } = await supabaseAdmin.from("user_plans").upsert(
      {
        user_id: data.userId,
        plan: data.plan,
        current_period_end: periodEnd,
        updated_at: new Date().toISOString(),
      },
      { onConflict: "user_id" },
    );
    if (error) throw new Error(error.message);

    const { error: historyError } = await supabaseAdmin.from("plan_history").insert({
      user_id: data.userId,
      old_plan: before?.plan ?? "free",
      new_plan: data.plan,
      months: data.months,
      period_end: periodEnd,
      paid: data.paid ?? false,
      source: "admin",
      billing: data.billing,
      changed_by: context.userId,
    });
    // Historiken styr vad som visas i admin (gåva kontra betalning) – tyst fel gör listan felaktig.
    if (historyError) console.error("[admin] plan_history sparades inte:", historyError.message);


    // Kvitton sparas separat och överlever en kontoradering (bokföringslagen: sju år).
    if (data.paid) {
      const { PLANS } = await import("@/lib/plans");
      const def = PLANS[data.plan as PlanId];
      const monthly = def?.priceSek ?? 0;
      const yearly = def?.priceYearSek ?? monthly * 12;
      const { data: userRes } = await supabaseAdmin.auth.admin.getUserById(data.userId);
      await supabaseAdmin.from("billing_receipts").insert({
        customer_ref: data.userId,
        email: userRes?.user?.email ?? "",
        plan: data.plan,
        months: data.months,
        amount_ore:
          data.billing === "yearly"
            ? Math.round((yearly / 12) * Math.max(data.months, 1)) * 100
            : monthly * Math.max(data.months, 1) * 100,
        period_start: new Date().toISOString(),
        period_end: periodEnd,
        source: "admin",
      });
    }


    // Besked till användaren: notis i appen + mejl. Ett misslyckat mejl får
    // aldrig fälla själva planändringen.
    let notified = false;
    if (data.notify) {
      const { buildPlanNotice } = await import("@/lib/plan-notice");
      const fallback = buildPlanNotice({
        oldPlan: (before?.plan ?? "free") as PlanId,
        newPlan: data.plan,
        billing: data.billing,
        periodEnd,
        paid: data.paid ?? false,
      });
      const subject = data.noticeSubject?.trim() || fallback.subject;
      const body = data.noticeBody?.trim() || fallback.body;

      await supabaseAdmin.from("system_notifications").insert({
        user_id: data.userId,
        kind: "plan_changed",
        title: subject,
        body,
        link: "/min-niva",
      });

      try {
        const { queuePush } = await import("@/lib/push.server");
        await queuePush({
          userId: data.userId,
          kind: "system",
          title: subject,
          body: body.slice(0, 160),
          url: "/konto",
        });
      } catch (pushError) {
        console.error("[admin] push misslyckades:", pushError);
      }

      try {
        const { emailTemplate } = await import("@/lib/email-templates.server");
        const { sendEmail } = await import("@/lib/email.server");
        const tpl = emailTemplate("plan_changed");
        const { data: userRes } = await supabaseAdmin.auth.admin.getUserById(data.userId);
        const to = userRes?.user?.email ?? "";
        if (tpl && to) {
          const built = tpl.build({ subject, heading: subject, body });
          await sendEmail({
            to,
            subject: built.subject,
            html: built.html,
            template: "plan_changed",
            reason: "Administratören ändrade kontotyp och valde att meddela användaren.",
            userId: data.userId,
          });
          notified = true;
        }
      } catch (err) {
        console.error("plan_changed email failed", err);
      }
    }

    return { ok: true, plan: data.plan, current_period_end: periodEnd, notified };
  });

/** Full profil för en användare: konto, kontotypshistorik och annonser. */
export const adminGetUser = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => z.object({ userId: z.string().uuid() }).parse(input))
  .handler(async ({ data, context }) => {
    await assertAdmin(context as any);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const { data: userRes, error } = await supabaseAdmin.auth.admin.getUserById(data.userId);
    if (error || !userRes?.user) throw new Error("Användaren hittades inte.");
    const u = userRes.user;
    const meta = (u.user_metadata ?? {}) as Record<string, unknown>;

    // Profilens valda visningsnamn går alltid först, metadata som reserv.
    const { data: profileRow } = await supabaseAdmin
      .from("profiles")
      .select("display_name")
      .eq("user_id", data.userId)
      .maybeSingle();

    const displayName =
      (((profileRow as any)?.display_name as string) || "").trim() ||
      (meta["display_name"] as string) ||
      (meta["full_name"] as string) ||
      (meta["name"] as string) ||
      null;

    const [
      { data: roles },
      { data: plan },
      { data: history },
      { data: ads },
      { data: devices },
    ] = await Promise.all([
      supabaseAdmin.from("user_roles").select("role").eq("user_id", data.userId),
      supabaseAdmin
        .from("user_plans")
        .select("plan, current_period_end, created_at, updated_at")
        .eq("user_id", data.userId)
        .maybeSingle(),
      supabaseAdmin
        .from("plan_history")
        .select("*")
        .eq("user_id", data.userId)
        .order("created_at", { ascending: false }),
      supabaseAdmin
        .from("ads")
        .select("slug, title, price, category, location, created_at, is_paused, promoted_until")
        .eq("user_id", data.userId)
        .order("created_at", { ascending: false })
        .limit(200),
      supabaseAdmin
        .from("user_devices")
        .select("id, platform, app_version, device_name, user_agent, push_endpoint, last_seen_at, created_at")
        .eq("user_id", data.userId)
        .order("last_seen_at", { ascending: false }),
    ]);

    // Cookie-val och godkända villkor för användaren.
    const [{ data: prefs }, { data: consent }] = await Promise.all([
      supabaseAdmin
        .from("user_preferences")
        .select("cookie_analytics, cookie_consent_at, theme_mode, notify_messages, notify_ad_expiry")
        .eq("user_id", data.userId)
        .maybeSingle(),
      supabaseAdmin
        .from("user_consents")
        .select("terms_version, privacy_version, accepted_at")
        .eq("user_id", data.userId)
        .order("accepted_at", { ascending: false })
        .limit(1)
        .maybeSingle(),
    ]);

    // Aktivitet: vilka sidor och annonser användaren varit inne på.
    const { data: events } = await supabaseAdmin
      .from("site_events")
      .select("kind, path, title, label, ad_slug, created_at, device, is_pwa")
      .eq("user_id", data.userId)
      .order("created_at", { ascending: false })
      .limit(1500);

    const rows = (events ?? []) as any[];

    // Senast besökta annonser, en rad per annons.
    const seenAds = new Map<string, { slug: string; at: string; views: number }>();
    for (const e of rows) {
      if (!e.ad_slug) continue;
      const prev = seenAds.get(e.ad_slug);
      if (prev) prev.views += 1;
      else seenAds.set(e.ad_slug, { slug: e.ad_slug, at: e.created_at, views: 1 });
    }
    const adSlugs = [...seenAds.keys()].slice(0, 40);
    const { data: adTitles } = adSlugs.length
      ? await supabaseAdmin.from("ads").select("slug, title, price").in("slug", adSlugs)
      : { data: [] as any[] };
    const titleBySlug = new Map((adTitles ?? []).map((a: any) => [a.slug, a]));
    const recentAds = [...seenAds.values()]
      .slice(0, 40)
      .map((a) => ({
        slug: a.slug,
        at: a.at,
        views: a.views,
        title: (titleBySlug.get(a.slug) as any)?.title ?? null,
        price: (titleBySlug.get(a.slug) as any)?.price ?? null,
      }));

    // Besökta sidor, sammanslagna per sökväg.
    const pageMap = new Map<string, { path: string; title: string; visits: number; last: string }>();
    for (const e of rows) {
      if (e.kind !== "pageview" && e.kind !== "ad_view") continue;
      const path = (e.path as string) || "/";
      const prev = pageMap.get(path);
      if (prev) prev.visits += 1;
      else pageMap.set(path, { path, title: e.title ?? "", visits: 1, last: e.created_at });
    }
    const pages = [...pageMap.values()].sort((a, b) => b.visits - a.visits).slice(0, 40);

    const anyPath = (re: RegExp) => rows.find((e) => re.test(String(e.path ?? "")));
    const anyKind = (kind: string) => rows.find((e) => e.kind === kind);
    const feature = (hit: any | undefined) => (hit ? { used: true, at: hit.created_at } : { used: false, at: null });

    const features = {
      priser: feature(anyPath(/^\/priser/)),
      videoskanning: feature(anyPath(/^\/(scanna|filma|video)/)),
      minaPrylar: feature(anyPath(/^\/mina-prylar/)),
      skapaAnnons: feature(anyKind("publish_start")),
      publicerat: feature(anyKind("publish_done")),
      meddelanden: feature(anyPath(/^\/meddelanden/)),
      oversikt: feature(anyPath(/^\/oversikt/)),
      konto: feature(anyPath(/^\/konto/)),
      sokt: feature(anyKind("search")),
      kontaktatSaljare: feature(anyKind("contact")),
      delat: feature(anyKind("share")),
      uppgraderingsklick: feature(anyKind("upgrade")),
      bilduppladdning: feature(anyKind("image_upload")),
      installeradApp: feature(rows.find((e) => e.is_pwa)),
    };

    const { data: lastChange } = await supabaseAdmin
      .from("plan_history")
      .select("paid, source, billing, created_at")
      .eq("user_id", data.userId)
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();

    return {
      planPaid: !!(lastChange as any)?.paid,
      planSource: ((lastChange as any)?.source as string) ?? null,
      planBilling: ((lastChange as any)?.billing as string) ?? null,
      themeMode: (prefs as any)?.theme_mode ?? null,
      notifyMessages: (prefs as any)?.notify_messages ?? null,
      notifyAdExpiry: (prefs as any)?.notify_ad_expiry ?? null,
      activity: {
        events: rows.length,
        firstAt: rows.length ? rows[rows.length - 1].created_at : null,
        lastAt: rows.length ? rows[0].created_at : null,
        recentAds,
        pages,
        features,
      },
      id: u.id,
      email: u.email ?? "",
      displayName,
      phone: (meta["phone"] as string) ?? null,
      createdAt: u.created_at,
      lastSignInAt: u.last_sign_in_at ?? null,
      confirmed: !!u.email_confirmed_at,
      // Alla inloggningssätt kontot faktiskt har kopplat, inte bara det senaste.
      provider:
        ((u.identities ?? []) as any[]).map((i) => i.provider).filter(Boolean).join(", ") ||
        ((u.app_metadata?.provider as string) ?? "email"),
      providerSince:
        ((u.identities ?? []) as any[])
          .map((i) => i.created_at as string)
          .filter(Boolean)
          .sort()[0] ?? null,

      roles: (roles ?? []).map((r: { role: string }) => r.role),
      plan: (plan?.plan as string) ?? "free",
      currentPeriodEnd: (plan?.current_period_end as string) ?? null,
      planSince: (plan?.updated_at as string) ?? null,
      planHistory: (history ?? []) as any[],
      ads: (ads ?? []) as any[],
      adsCount: (ads ?? []).length,
      devices: (devices ?? []) as any[],
      cookieAnalytics: (prefs as any)?.cookie_analytics ?? null,
      cookieConsentAt: (prefs as any)?.cookie_consent_at ?? null,
      termsVersion: (consent as any)?.terms_version ?? null,
      termsAcceptedAt: (consent as any)?.accepted_at ?? null,
    };
  });


/** Versionshistorik för en annons, med nuvarande värden. */
export const adminGetAdVersions = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => z.object({ slug: z.string().min(1) }).parse(input))
  .handler(async ({ data, context }) => {
    await assertAdmin(context as any);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const { data: ad, error } = await supabaseAdmin
      .from("ads")
      .select(
        "id, slug, title, description, price, category, location, user_id, created_at, updated_at, is_paused, sold_at, archived_at, expires_at, image_url, image_urls, video_url, tags",
      )
      .eq("slug", data.slug)
      .maybeSingle();
    if (error || !ad) throw new Error("Annonsen hittades inte.");


    const { data: history } = await supabaseAdmin
      .from("ad_history")
      .select("id, field, old_value, new_value, created_at, changed_by, changed_by_admin")
      .eq("ad_id", ad.id)
      .order("created_at", { ascending: false });

    // Vem gjorde ändringen? Bara admin ser detta.
    const editorIds = Array.from(
      new Set((history ?? []).map((h: any) => h.changed_by).filter(Boolean)),
    ) as string[];
    const editors = new Map<string, string>();
    await Promise.all(
      editorIds.map(async (id) => {
        const { data: u } = await supabaseAdmin.auth.admin.getUserById(id);
        if (u?.user?.email) editors.set(id, u.user.email);
      }),
    );

    let owner: {
      id: string;
      name: string | null;
      email: string | null;
      adsCount: number;
    } | null = null;

    if (ad.user_id) {
      const [{ data: profile }, userRes, { count }] = await Promise.all([
        supabaseAdmin
          .from("profiles")
          .select("display_name")
          .eq("user_id", ad.user_id)
          .maybeSingle(),
        supabaseAdmin.auth.admin.getUserById(ad.user_id),
        supabaseAdmin
          .from("ads")
          .select("id", { count: "exact", head: true })
          .eq("user_id", ad.user_id),
      ]);
      owner = {
        id: ad.user_id as string,
        name: ((profile as any)?.display_name as string) || null,
        email: userRes?.data?.user?.email ?? null,
        adsCount: Number(count ?? 0),
      };
    }

    return {
      ad,
      history: ((history ?? []) as any[]).map((h) => ({
        ...h,
        editor_email: h.changed_by ? (editors.get(h.changed_by) ?? null) : null,
      })),
      owner,
    };
  });

/** Återställer ett fält på en annons till ett tidigare värde. */
export const adminRevertAdField = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z
      .object({
        slug: z.string().min(1),
        field: z.enum(["title", "description", "price", "category", "location"]),
        value: z.string().max(4000),
      })
      .parse(input),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context as any);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const patch: Record<string, unknown> = {};
    if (data.field === "price") {
      const n = Number(data.value);
      if (!Number.isFinite(n) || n <= 0) throw new Error("Ogiltigt pris.");
      patch["price"] = Math.round(n);
    } else {
      if (!data.value.trim()) throw new Error("Tomt värde kan inte återställas.");
      patch[data.field] = data.value;
    }

    const since = new Date(Date.now() - 5000).toISOString();
    const { error } = await supabaseAdmin
      .from("ads")
      .update(patch as never)
      .eq("slug", data.slug);
    if (error) throw new Error(error.message);
    const { stampAdminHistory } = await import("./ad-history.server");
    await stampAdminHistory(data.slug, context.userId, since);
    return { ok: true };
  });

/**
 * Granskar en annons på nytt med AI utan att spara något.
 * Admin skickar med det som står i formuläret just nu, även osparat.
 */
export const adminReviewAd = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z
      .object({
        slug: z.string().min(1),
        title: z.string().max(200).default(""),
        description: z.string().max(6000).default(""),
        price: z.number().int().nonnegative().default(0),
        category: z.string().max(60).default("ovrigt"),
        location: z.string().max(120).default(""),
      })
      .parse(input),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context as any);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const { data: ad, error } = await supabaseAdmin
      .from("ads")
      .select("image_url, image_urls, tags")
      .eq("slug", data.slug)
      .maybeSingle();
    if (error || !ad) throw new Error("Annonsen hittades inte.");

    const paths = (((ad as any).image_urls?.length ? (ad as any).image_urls : [(ad as any).image_url]) as string[])
      .filter(Boolean)
      .slice(0, 6);
    if (!paths.length) throw new Error("Annonsen saknar bilder att granska.");

    const images: { imageBase64: string; mimeType: string }[] = [];
    for (const path of paths) {
      const { data: blob } = await supabaseAdmin.storage.from("ad-images").download(path);
      if (!blob) continue;
      const buffer = Buffer.from(await blob.arrayBuffer());
      images.push({ imageBase64: buffer.toString("base64"), mimeType: blob.type || "image/jpeg" });
    }
    if (!images.length) throw new Error("Kunde inte läsa annonsens bilder.");

    const tags = (((ad as any).tags ?? []) as string[]).filter(Boolean).slice(0, 8);

    // Samma marknadsminne som djupvärderingen använder, så priset bygger på
    // faktiska fynd i stället för en fri gissning.
    const { lookupMarketMemory, memoryContext, memoryPriceBand } = await import(
      "./market-memory.server"
    );
    const descriptor = tags.join(" ");
    const [hit, band] = await Promise.all([
      lookupMarketMemory({
        query: `${data.title} ${descriptor}`.trim(),
        name: data.title,
        descriptor,
        category: data.category,
      }).catch(() => null),
      memoryPriceBand({ name: data.title, descriptor, category: data.category }).catch(() => null),
    ]);

    const { runAdReview } = await import("./ad-review.server");
    const review = await runAdReview(
      images,
      {
        title: data.title,
        description: data.description,
        price: data.price,
        category: data.category,
        location: data.location,
      },
      context.userId,
      {
        context: memoryContext(hit),
        band,
        sources: (hit?.sources ?? []).slice(0, 6).map((s) => ({ url: s.url, title: s.title })),
      },
      tags,
    );
    return {
      review,
      imageCount: images.length,
      market: {
        band,
        sources: (hit?.sources ?? []).slice(0, 6).map((s) => ({ url: s.url, title: s.title })),
        ageDays: hit?.ageDays ?? null,
      },
    };
  });

/**
 * Kör bildstudion (rensa bakgrund / studiobild / ny vinkel) på en av
 * annonsens bilder. Admin-only och utan kvot – säljarens kvot rörs inte.
 */
export const adminCleanAdImage = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z
      .object({
        imageBase64: z.string().min(100).max(11_000_000),
        mimeType: z.string().min(3).max(80),
        mode: z.enum(["background", "studio", "angle"]).default("background"),
      })
      .parse(input),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context as any);
    if (!/^image\//i.test(data.mimeType)) throw new Error("Endast bilder kan bearbetas.");

    const { cleanImageBackground, CLEAN_IMAGE_MODEL } = await import("./clean-image.server");
    const { trackAiCall } = await import("./ai-usage.server");
    const result = await trackAiCall(
      { operation: "clean_image_admin", model: CLEAN_IMAGE_MODEL, userId: context.userId },
      async () => {
        const out = await cleanImageBackground(data.imageBase64, data.mimeType, data.mode);
        return { ...out, usage: out.usage };
      },
    );
    return { imageBase64: result.imageBase64, mimeType: result.mimeType, mode: data.mode };
  });

/** Sparar en bearbetad bild tillbaka på annonsen (ersätter bilden på given plats). */
export const adminSaveAdImage = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z
      .object({
        slug: z.string().min(1),
        index: z.number().int().min(0).max(19),
        imageBase64: z.string().min(100).max(11_000_000),
        mimeType: z.string().min(3).max(80),
      })
      .parse(input),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context as any);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const { data: ad, error } = await supabaseAdmin
      .from("ads")
      .select("id, image_url, image_urls")
      .eq("slug", data.slug)
      .maybeSingle();
    if (error || !ad) throw new Error("Annonsen hittades inte.");

    const current = (((ad as any).image_urls?.length
      ? (ad as any).image_urls
      : [(ad as any).image_url]) as string[]).filter(Boolean);
    if (data.index >= current.length) throw new Error("Bilden finns inte längre.");

    const { uploadImageToStorage } = await import("./ads.server");
    const path = await uploadImageToStorage(data.imageBase64, data.mimeType);

    const next = [...current];
    next[data.index] = path;

    const { error: updateError } = await supabaseAdmin
      .from("ads")
      .update({ image_urls: next, image_url: next[0] } as never)
      .eq("id", (ad as any).id);
    if (updateError) throw new Error(updateError.message);

    return { imageUrls: next };
  });



/** Interna AI-taggar för en annons (endast admin). */
export const adminGetAdTags = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => z.object({ slug: z.string().min(1) }).parse(input))
  .handler(async ({ data, context }) => {
    await assertAdmin(context as any);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: ad, error } = await supabaseAdmin
      .from("ads")
      .select("slug, tags")
      .eq("slug", data.slug)
      .maybeSingle();
    if (error || !ad) throw new Error("Annonsen hittades inte.");
    return { tags: ((ad as any).tags as string[] | null) ?? [] };
  });

/** Sparar interna taggar för en annons (endast admin). */
export const adminSetAdTags = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z.object({ slug: z.string().min(1), tags: z.array(z.string().max(40)).max(12) }).parse(input),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context as any);
    const { normalizeTags } = await import("./ads.server");
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const tags = normalizeTags(data.tags);
    const { error } = await supabaseAdmin
      .from("ads")
      .update({ tags } as never)
      .eq("slug", data.slug);
    if (error) throw new Error(error.message);
    return { tags };
  });

/** Genererar om taggarna med AI utifrån annonsens text (endast admin). */
export const adminRegenerateAdTags = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => z.object({ slug: z.string().min(1) }).parse(input))
  .handler(async ({ data, context }) => {
    await assertAdmin(context as any);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: ad, error } = await supabaseAdmin
      .from("ads")
      .select("id, title, description, category")
      .eq("slug", data.slug)
      .maybeSingle();
    if (error || !ad) throw new Error("Annonsen hittades inte.");

    const { generateTagsFromText, fetchTagVocabulary } = await import("./tags.server");
    const { runWithAiContext } = await import("./ai-usage.server");
    const vocabulary = await fetchTagVocabulary();
    const tags = await runWithAiContext(
      { operation: "tags", userId: (context as any)?.userId ?? null, adId: (ad as any).id },
      () =>
        generateTagsFromText({
          title: ad.title,
          description: ad.description,
          category: ad.category,
          existingTags: vocabulary,
        }),
    );

    const { error: upErr } = await supabaseAdmin
      .from("ads")
      .update({ tags } as never)
      .eq("slug", data.slug);
    if (upErr) throw new Error(upErr.message);
    return { tags };
  });

/** Listar anmälda annonser med anmälare, anledning och kommentar (endast admin). */
export const adminListReports = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z.object({ status: z.string().max(20).optional() }).parse(input ?? {}),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context as any);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    let query = supabaseAdmin
      .from("ad_reports")
      .select(
        "id, ad_id, reporter_id, reason, comment, status, admin_note, created_at, updated_at, ai_priority, ai_summary, ai_risks",
      )
      .order("created_at", { ascending: false })
      .limit(200);
    if (data.status && data.status !== "all") query = query.eq("status", data.status);

    const { data: rows, error } = await query;
    if (error) throw new Error(error.message);
    const reports = rows ?? [];

    const adIds = [...new Set(reports.map((r) => r.ad_id))];
    const ads = adIds.length
      ? ((
          await supabaseAdmin
            .from("ads")
            .select("id, slug, title, price, category, location, is_paused, user_id")
            .in("id", adIds)
        ).data ?? [])
      : [];
    const adById = new Map(ads.map((a) => [a.id, a]));

    const reporterIds = [...new Set(reports.map((r) => r.reporter_id))];
    const reporters = new Map<string, { email: string | null; name: string | null }>();
    await Promise.all(
      reporterIds.map(async (id) => {
        const { data: res } = await supabaseAdmin.auth.admin.getUserById(id);
        const u = res?.user;
        reporters.set(id, {
          email: u?.email ?? null,
          name:
            (u?.user_metadata?.["display_name"] as string | undefined) ??
            (u?.user_metadata?.["full_name"] as string | undefined) ??
            null,
        });
      }),
    );

    const { count: openCount } = await supabaseAdmin
      .from("ad_reports")
      .select("id", { count: "exact", head: true })
      .eq("status", "new");

    const { count: urgentCount } = await supabaseAdmin
      .from("ad_reports")
      .select("id", { count: "exact", head: true })
      .eq("status", "new")
      .in("ai_priority", ["hog", "akut"]);

    return {
      openCount: openCount ?? 0,
      urgentCount: urgentCount ?? 0,
      reports: reports.map((r) => ({
        ...r,
        ad: adById.get(r.ad_id) ?? null,
        reporter: reporters.get(r.reporter_id) ?? { email: null, name: null },
      })),
    };
  });

/** Uppdaterar status och anteckning för en anmälan (endast admin). */
export const adminUpdateReport = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z
      .object({
        id: z.string().uuid(),
        status: z.enum(["new", "reviewed", "actioned", "dismissed"]).optional(),
        adminNote: z.string().max(2000).optional(),
      })
      .parse(input),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context as any);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const patch: Record<string, unknown> = {};
    if (data.status) patch.status = data.status;
    if (data.adminNote !== undefined) patch.admin_note = data.adminNote;
    const { error } = await supabaseAdmin
      .from("ad_reports")
      .update(patch as never)
      .eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

/** Pausar eller återaktiverar en annons från administrationen. */
export const adminSetAdPaused = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z.object({ slug: z.string().min(1), paused: z.boolean() }).parse(input),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context as any);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { error } = await supabaseAdmin
      .from("ads")
      .update({ is_paused: data.paused } as never)
      .eq("slug", data.slug);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

/** Listar anmälda meddelanden för administratörer. */
export const adminListMessageReports = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z.object({ status: z.string().max(20).optional() }).parse(input ?? {}),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context as any);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    let query = supabaseAdmin
      .from("message_reports")
      .select(
        "id, message_id, ad_id, reporter_id, reported_user_id, reason, comment, message_snapshot, status, admin_note, created_at",
      )
      .order("created_at", { ascending: false })
      .limit(200);
    if (data.status && data.status !== "all") query = query.eq("status", data.status);

    const { data: rows, error } = await query;
    if (error) throw new Error(error.message);
    const reports = rows ?? [];

    const adIds = [...new Set(reports.map((r: any) => r.ad_id))];
    const ads = adIds.length
      ? ((await supabaseAdmin.from("ads").select("id, slug, title, user_id").in("id", adIds))
          .data ?? [])
      : [];
    const adById = new Map(ads.map((a: any) => [a.id, a]));

    const userIds = [...new Set(reports.flatMap((r: any) => [r.reporter_id, r.reported_user_id]))];
    const users = new Map<string, { email: string | null; name: string | null }>();
    await Promise.all(
      userIds.map(async (id: string) => {
        const { data: res } = await supabaseAdmin.auth.admin.getUserById(id);
        const u = res?.user;
        users.set(id, {
          email: u?.email ?? null,
          name:
            (u?.user_metadata?.["display_name"] as string | undefined) ??
            (u?.user_metadata?.["full_name"] as string | undefined) ??
            null,
        });
      }),
    );

    const { count: openCount } = await supabaseAdmin
      .from("message_reports")
      .select("id", { count: "exact", head: true })
      .eq("status", "new");

    return {
      openCount: openCount ?? 0,
      reports: reports.map((r: any) => ({
        ...r,
        ad: adById.get(r.ad_id) ?? null,
        reporter: users.get(r.reporter_id) ?? null,
        reported: users.get(r.reported_user_id) ?? null,
      })),
    };
  });

/** Uppdaterar status eller intern anteckning på en meddelandeanmälan. */
export const adminUpdateMessageReport = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z
      .object({
        id: z.string().uuid(),
        status: z.enum(["new", "reviewed", "actioned", "dismissed"]).optional(),
        adminNote: z.string().max(2000).optional(),
      })
      .parse(input),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context as any);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const patch: Record<string, unknown> = {};
    if (data.status) patch.status = data.status;
    if (data.adminNote !== undefined) patch.admin_note = data.adminNote;
    const { error } = await supabaseAdmin
      .from("message_reports")
      .update(patch as never)
      .eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

/**
 * Adminöversikt för en enskild användare – används i hover-bubblan som visas
 * när en admin pekar på ett användarnamn någonstans i appen.
 */
export const adminUserInsights = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => z.object({ userId: z.string().uuid() }).parse(input))
  .handler(async ({ data, context }) => {
    await assertAdmin(context as any);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const id = data.userId;

    const [{ data: profile }, { data: authUser }, { data: plan }] = await Promise.all([
      supabaseAdmin
        .from("profiles")
        .select("display_name, avatar_url, updated_at")
        .eq("user_id", id)
        .maybeSingle(),
      supabaseAdmin.auth.admin.getUserById(id),
      supabaseAdmin
        .from("user_plans")
        .select("plan, current_period_end")
        .eq("user_id", id)
        .maybeSingle(),
    ]);

    const user: any = (authUser as any)?.user ?? null;

    const countRows = async (
      table: "ads" | "support_tickets" | "ad_reports",
      apply: (q: any) => any,
    ) => {
      const { count } = await apply(
        supabaseAdmin.from(table).select("id", { count: "exact", head: true }),
      );
      return (count as number | null) ?? 0;
    };

    const [adsTotal, adsActive, tickets, reports] = await Promise.all([
      countRows("ads", (q) => q.eq("user_id", id)),
      countRows("ads", (q) =>
        q.eq("user_id", id).eq("is_paused", false).is("sold_at", null).is("archived_at", null),
      ),
      countRows("support_tickets", (q) => q.eq("user_id", id)),
      countRows("ad_reports", (q) => q.eq("reporter_id", id)),
    ]);

    // Senaste orten säljaren annonserat från – ger snabb känsla för var hen finns.
    const { data: lastAd } = await supabaseAdmin
      .from("ads")
      .select("location, created_at")
      .eq("user_id", id)
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();

    const { previousNames } = await import("./display-names.server");
    const previous = await previousNames([id]).catch(() => new Map<string, string>());

    const { data: roleRows } = await supabaseAdmin
      .from("user_roles")
      .select("role")
      .eq("user_id", id);

    const name =
      ((profile as any)?.display_name as string | null)?.trim() ||
      (user?.user_metadata?.["display_name"] as string | undefined) ||
      (user?.email as string | undefined)?.split("@")[0] ||
      "Användare";
    const prev = previous.get(id) ?? null;

    return {
      userId: id,
      name,
      previousName: prev && prev !== name ? prev : null,
      avatarUrl: ((profile as any)?.avatar_url as string | null) ?? null,
      email: (user?.email as string | undefined) ?? null,
      plan: ((plan as any)?.plan as string | null) ?? "free",
      periodEnd: ((plan as any)?.current_period_end as string | null) ?? null,
      roles: ((roleRows ?? []) as { role: string }[]).map((r) => r.role),
      createdAt: (user?.created_at as string | undefined) ?? null,
      lastSignInAt: (user?.last_sign_in_at as string | undefined) ?? null,
      confirmed: !!user?.email_confirmed_at,
      providers: ((user?.app_metadata?.["providers"] as string[] | undefined) ?? []).filter(Boolean),
      location: ((lastAd as any)?.location as string | null) ?? null,
      adsTotal,
      adsActive,
      tickets,
      reports,
    };
  });

/** Adminöversikt för en enskild annons – används i hover-bubblan på annonskort. */

export const adminAdInsights = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z
      .object({ slug: z.string().min(1), referenceSlug: z.string().min(1).nullish() })
      .parse(input),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context as any);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const { data: ad, error } = await supabaseAdmin
      .from("ads")
      .select(
        "id, slug, title, category, price, user_id, contact_name, contact_email, created_at, updated_at, expires_at, is_paused, promoted_until, sold_at, archived_at, tags, image_urls, video_url",
      )

      .eq("slug", data.slug)
      .maybeSingle();
    if (error || !ad) throw new Error("Annonsen hittades inte.");

    const a = ad as any;
    const tags: string[] = (a.tags as string[] | null) ?? [];

    const { data: history } = await supabaseAdmin
      .from("ad_history")
      .select("field, created_at")
      .eq("ad_id", a.id)
      .order("created_at", { ascending: false })
      .limit(20);

    let displayName: string | null = null;
    if (a.user_id) {
      const { data: profile } = await supabaseAdmin
        .from("profiles")
        .select("display_name")
        .eq("user_id", a.user_id)
        .maybeSingle();
      displayName = (profile as any)?.display_name ?? null;
    }

    let matchedTags: string[] = [];
    let referenceCategory: string | null = null;
    if (data.referenceSlug && data.referenceSlug !== data.slug) {
      const { data: ref } = await supabaseAdmin
        .from("ads")
        .select("tags, category")
        .eq("slug", data.referenceSlug)
        .maybeSingle();
      const refTags = new Set(((ref as any)?.tags as string[] | null) ?? []);
      matchedTags = tags.filter((t) => refTags.has(t));
      referenceCategory = (ref as any)?.category ?? null;
    }

    const { adCostBreakdown } = await import("./ad-costs.server");
    const cost = await adCostBreakdown({
      adId: a.id as string,
      imagePaths: ((a.image_urls as string[] | null) ?? []).filter(Boolean),
      videoPath: (a.video_url as string | null) ?? null,
      hasTags: tags.length > 0,
    });

    const changes = (history ?? []) as Array<{ field: string; created_at: string }>;
    return {
      cost,

      slug: a.slug,
      createdAt: a.created_at as string,
      updatedAt: a.updated_at as string | null,
      expiresAt: a.expires_at as string | null,
      isPaused: !!a.is_paused,
      promotedUntil: a.promoted_until as string | null,
      soldAt: a.sold_at as string | null,
      archivedAt: a.archived_at as string | null,
      ownerName: displayName ?? (a.contact_name as string | null),
      ownerEmail: a.contact_email as string | null,
      ownerId: a.user_id as string | null,
      category: a.category as string | null,
      tags,
      changeCount: changes.length,
      lastChange: changes[0] ?? null,
      changedFields: Array.from(new Set(changes.map((c) => c.field))),
      matchedTags,
      matchesCategory: referenceCategory != null && referenceCategory === a.category,
      hasReference: !!data.referenceSlug && data.referenceSlug !== data.slug,
    };
  });

/** Faktisk AI-användning: per dag och per operation. */
export const adminAiUsage = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z.object({ days: z.number().int().min(1).max(180).default(30) }).parse(input ?? {}),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context as any);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const since = new Date();
    since.setHours(0, 0, 0, 0);
    since.setDate(since.getDate() - (data.days - 1));

    const rows: Array<{
      created_at: string;
      operation: string;
      model: string;
      credits: number;
      input_tokens: number;
      output_tokens: number;
      ok: boolean;
      duration_ms: number;
      user_id: string | null;
    }> = [];

    // Hämta i sidor så att långa perioder inte tappar rader.
    for (let page = 0; page < 40; page++) {
      const { data: chunk, error } = await supabaseAdmin
        .from("ai_calls")
        .select("created_at, operation, model, credits, input_tokens, output_tokens, ok, duration_ms, user_id")
        .gte("created_at", since.toISOString())
        .order("created_at", { ascending: true })
        .range(page * 1000, page * 1000 + 999);
      if (error) throw new Error(`Kunde inte hämta AI-användning: ${error.message}`);
      rows.push(...((chunk ?? []) as typeof rows));
      if (!chunk || chunk.length < 1000) break;
    }

    const dayKey = (iso: string) => new Date(iso).toISOString().slice(0, 10);

    const byDay = new Map<
      string,
      { day: string; calls: number; credits: number; inputTokens: number; outputTokens: number; failed: number }
    >();
    for (let i = 0; i < data.days; i++) {
      const d = new Date(since);
      d.setDate(d.getDate() + i);
      const key = d.toISOString().slice(0, 10);
      byDay.set(key, { day: key, calls: 0, credits: 0, inputTokens: 0, outputTokens: 0, failed: 0 });
    }

    const byOperation = new Map<
      string,
      {
        operation: string;
        calls: number;
        credits: number;
        failed: number;
        totalMs: number;
        users: Set<string>;
      }
    >();

    const todayKey = new Date().toISOString().slice(0, 10);
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayKey = yesterday.toISOString().slice(0, 10);

    let totalCalls = 0;
    let totalCredits = 0;
    let totalFailed = 0;

    for (const row of rows) {
      const key = dayKey(row.created_at);
      const day = byDay.get(key);
      if (day) {
        day.calls += 1;
        day.credits += Number(row.credits) || 0;
        day.inputTokens += row.input_tokens ?? 0;
        day.outputTokens += row.output_tokens ?? 0;
        if (!row.ok) day.failed += 1;
      }
      const op = byOperation.get(row.operation) ?? {
        operation: row.operation,
        calls: 0,
        credits: 0,
        failed: 0,
        totalMs: 0,
        users: new Set<string>(),
      };
      op.calls += 1;
      if (row.user_id) op.users.add(row.user_id);
      op.credits += Number(row.credits) || 0;
      op.totalMs += row.duration_ms ?? 0;
      if (!row.ok) op.failed += 1;
      byOperation.set(row.operation, op);

      totalCalls += 1;
      totalCredits += Number(row.credits) || 0;
      if (!row.ok) totalFailed += 1;
    }

    const days = Array.from(byDay.values()).sort((a, b) => a.day.localeCompare(b.day));
    const today = byDay.get(todayKey);
    const yesterdayRow = byDay.get(yesterdayKey);

    return {
      days,
      operations: Array.from(byOperation.values())
        .map(({ users, ...o }) => ({
          ...o,
          users: users.size,
          avgMs: o.calls ? Math.round(o.totalMs / o.calls) : 0,
        }))
        .sort((a, b) => b.credits - a.credits),
      totals: {
        calls: totalCalls,
        credits: totalCredits,
        failed: totalFailed,
        todayCalls: today?.calls ?? 0,
        todayCredits: today?.credits ?? 0,
        yesterdayCalls: yesterdayRow?.calls ?? 0,
        yesterdayCredits: yesterdayRow?.credits ?? 0,
      },
    };
  });

/** Lista över konton som är på väg att raderas, med hela loggen. */
export const adminListDeletions = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z
      .object({ status: z.enum(["pending", "cancelled", "completed", "all"]).default("pending") })
      .parse(input ?? {}),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context as any);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    let query = supabaseAdmin
      .from("account_deletion_requests")
      .select("*")
      .order("requested_at", { ascending: false })
      .limit(200);
    if (data.status !== "all") query = query.eq("status", data.status);

    const { data: rows, error } = await query;
    if (error) throw new Error(error.message);
    return { rows: rows ?? [] };
  });

/** Återställer ett konto som är på väg att raderas. */
export const adminCancelDeletion = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => z.object({ userId: z.string().uuid() }).parse(input))
  .handler(async ({ data, context }) => {
    await assertAdmin(context as any);
    const { cancelAccountDeletion } = await import("@/lib/account-deletion.server");
    return cancelAccountDeletion(data.userId, context.userId);
  });

/** Raderar ett konto direkt, utan att vänta ut fristen. */
export const adminPurgeNow = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => z.object({ userId: z.string().uuid() }).parse(input))
  .handler(async ({ data, context }) => {
    await assertAdmin(context as any);
    const { purgeAccount } = await import("@/lib/account-deletion.server");
    return purgeAccount(data.userId);
  });

/**
 * Dagens puls i administrationen: besökare, sidvisningar, registreringar,
 * nya annonser, obesvarad support och olästa meddelanden.
 */
export const getAdminPulse = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context as any);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    const today = startOfToday.toISOString();
    const yesterday = new Date(startOfToday.getTime() - 24 * 3600 * 1000).toISOString();

    const [events, yEvents, signups, ads, tickets, messages] = await Promise.all([
      supabaseAdmin
        .from("site_events")
        .select("visitor_id, kind")
        .gte("created_at", today)
        .eq("is_admin", false),
      supabaseAdmin
        .from("site_events")
        .select("visitor_id")
        .gte("created_at", yesterday)
        .lt("created_at", today)
        .eq("is_admin", false),
      supabaseAdmin
        .from("profiles")
        .select("user_id", { count: "exact", head: true })
        .gte("created_at", today),
      supabaseAdmin
        .from("ads")
        .select("id", { count: "exact", head: true })
        .gte("created_at", today),
      supabaseAdmin
        .from("support_tickets")
        .select("id", { count: "exact", head: true })
        .in("status", ["new", "open", "waiting"]),
      supabaseAdmin
        .from("messages")
        .select("id", { count: "exact", head: true })
        .is("read_at", null)
        .gte("created_at", yesterday),
    ]);

    const rows = (events.data ?? []) as { visitor_id: string; kind: string }[];
    const visitorsToday = new Set(rows.map((r) => r.visitor_id).filter(Boolean)).size;
    const pageviewsToday = rows.filter((r) => r.kind === "pageview").length;
    const visitorsYesterday = new Set(
      ((yEvents.data ?? []) as { visitor_id: string }[]).map((r) => r.visitor_id).filter(Boolean),
    ).size;

    return {
      visitorsToday,
      visitorsYesterday,
      pageviewsToday,
      signupsToday: signups.count ?? 0,
      adsToday: ads.count ?? 0,
      openTickets: tickets.count ?? 0,
      unreadMessages: messages.count ?? 0,
    };
  });

/** Påbörjade annonser (utkast) – vad säljare har på gång men inte publicerat. */
export const listAdminDrafts = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context as any);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const { data } = await supabaseAdmin
      .from("ad_drafts")
      .select("id, user_id, image_paths, payload, video, updated_at, created_at")
      .order("updated_at", { ascending: false })
      .limit(50);

    const rows = (data ?? []) as {
      id: string;
      user_id: string;
      image_paths: string[] | null;
      payload: any;
      video: any;
      updated_at: string;
      created_at: string;
    }[];

    const userIds = [...new Set(rows.map((r) => r.user_id))];
    const emails = new Map<string, string>();
    if (userIds.length) {
      const { data: users } = await supabaseAdmin.auth.admin.listUsers({ page: 1, perPage: 200 });
      for (const u of ((users as any)?.users ?? []) as { id: string; email?: string }[]) {
        if (u.email) emails.set(u.id, u.email);
      }
    }

    return rows.map((r) => {
      const form = (r.payload?.form ?? {}) as Record<string, string>;
      return {
        id: r.id,
        userId: r.user_id,
        email: emails.get(r.user_id) ?? null,
        title: (form.title ?? "").slice(0, 120),
        description: (form.description ?? "").slice(0, 4000),
        price: form.price ?? "",
        category: form.category ?? "",
        location: form.location ?? "",
        tags: (r.payload?.tags ?? []) as string[],
        images: (r.image_paths ?? []).slice(0, 6),
        hasVideo: Boolean(r.video),
        updatedAt: r.updated_at,
        createdAt: r.created_at,
      };
    });
  });
